/**
 * Normalise display text from CheckCarDetails API fields (often ALL CAPS).
 */

const PRESERVE_UPPER = new Set([
    "ABS",
    "USB",
    "DAB",
    "MOT",
    "PHV",
    "TDI",
    "TFSI",
    "TSI",
    "GTI",
    "GTE",
    "PHEV",
    "SE",
    "GT",
    "EV",
    "SUV",
    "MPV",
    "AWD",
    "4WD",
    "FWD",
    "RHD",
    "LHD",
    "UK",
    "EU",
    "BMW",
    "VW",
])

const FUEL_LABELS = {
    DIESEL: "Diesel",
    PETROL: "Petrol",
    ELECTRIC: "Electric",
    HYBRID: "Hybrid",
    "PETROL/ELECTRIC": "Petrol/Electric",
    "DIESEL/ELECTRIC": "Diesel/Electric",
    LPG: "LPG",
    CNG: "CNG",
}

const isMostlyUppercase = (text) => {
    const letters = text.replace(/[^a-zA-Z]/g, "")
    if (!letters) return false
    const upper = letters.replace(/[^A-Z]/g, "").length
    return upper / letters.length >= 0.8
}

const normalizeWord = (word) => {
    if (!word) return word
    if (/^\d+$/.test(word)) return word

    const bare = word.replace(/[^a-zA-Z0-9]/g, "")
    const upper = bare.toUpperCase()

    if (PRESERVE_UPPER.has(upper)) {
        return word.replace(bare, upper)
    }

    if (!isMostlyUppercase(bare)) {
        return word.charAt(0).toUpperCase() + word.slice(1)
    }

    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

/**
 * Convert shouty API strings to readable title case, preserving common acronyms.
 */
export const normalizeText = (value) => {
    if (value == null || value === "") return ""
    const text = String(value).trim()
    if (!text) return ""

    return text
        .split(/(\s+|\/)/)
        .map((part) => {
            if (part === "/" || /^\s+$/.test(part)) return part
            return normalizeWord(part)
        })
        .join("")
        .replace(/\s+/g, " ")
        .trim()
}

/**
 * Fuel types are a fixed vocabulary from the API.
 */
export const normalizeFuelType = (value) => {
    if (!value) return ""
    const key = String(value).trim().toUpperCase()
    return FUEL_LABELS[key] || normalizeText(value)
}

/**
 * Format ISO/API dates for display (e.g. 2018-06-26T00:00:00 → 26 June 2018).
 */
export const formatApiDate = (value, { month = "long" } = {}) => {
    if (!value) return ""
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return normalizeText(String(value))
    }
    return new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month,
        year: "numeric",
    }).format(parsed)
}

/**
 * VRMs should stay uppercase; do not pass through normalizeText.
 */
export const normalizeVrmDisplay = (vrm) =>
    String(vrm || "")
        .replace(/\s+/g, "")
        .toUpperCase()
